import { ComponentPublicInstance, ref } from "vue"
import { Base } from "./base"
import { dedent, kebabCase } from "./helper/string"
import { info } from "./init"

type Style = {
	[name: string]: string
}

type MessageStyle = {
	text: string
	style?: Style
}

type Message = MessageStyle | any

type ConsoleType = "log" | "info" | "trace" | "warn" | "error"

const history = ref<any>([])

class Log extends Base {
	public style: Style[] = []

	constructor(public type: ConsoleType = "log") {
		super()
	}

	get defaultStyle(): Style {
		return {
			color: "#546e7a",
			fontSize: "15px",
		}
	}

	trace(...messages: Message[]) {
		let m: string[] = []
		// m.push(phrase(this, "\n", this.defaultStyle))
		messages.forEach((message: Message) => {
			let msg: any = message
			if (message.text === undefined && typeof message !== "string") {
				msg = {
					text: `${JSON.stringify(message, null, 2)}`,
					style: {
						color: "#5e35b1",
						fontSize: "18px",
					},
				}
			}

			if (typeof msg === "string") {
				msg = {
					text: message,
					style: this.defaultStyle,
				}
			}
			if (!msg.style) {
				msg.style = this.defaultStyle
			}
			const factory = new MessageFactory(this, msg.text, msg.style)
			m.push(factory.toString())
		})

		const createInlineStyle = (list: Style) => {
			return Object.keys(list)
				.reduce((items, name) => {
					items.push(`${kebabCase(name)}:${list[name]}` as never)
					return items
				}, [])
				.join(";")
		}

		if (console.hasOwnProperty(this.type)) {
			console[this.type](
				m.join(""),
				...this.style.map((list: Style) => {
					return createInlineStyle(list)
				}),
			)
		}
		history.value.push({
			date: new Date().toLocaleString(),
			// message: m.join("").replace(/\%c/g, ""),
			message: m
				.map(
					(str, i) =>
						`<span style="${createInlineStyle(this.style[i])}">${str
							.replace(/\%c/g, "")
							.replace(/\n/g, "<br>")}</span>`,
				)
				.join(""),
			type: this.type,
		})
	}
}

class MessageFactory extends Base {
	constructor(public log: Log, public text: string, public style: Style) {
		super()
		log.style.push(style)
	}

	toString(): string {
		return `%c${this.text}`
	}
}

const phrase = (log: Log, text: string, style: Style) => {
	return new MessageFactory(log, text, style).toString()
}

const ln = {
	text: "\n",
	style: {},
}

function logType(...messages: Message[]) {
	const log = new Log("log")
	log.trace(...[...messages])
}

function infoType(...messages: Message[]) {
	const log = new Log("info")
	log.trace(...[...messages])
}

function traceType(...messages: Message[]) {
	const log = new Log("trace")
	log.trace(...[...messages])
}

function warnType(...messages: Message[]) {
	const log = new Log("warn")
	log.trace(...[...messages])
}

function errorType(...messages: Message[]) {
	const log = new Log("error")
	log.trace(...[...messages])
}

export function log(...messages: Message[]) {
	return logType(...messages)
}

log.info = infoType
log.trace = traceType
log.warn = warnType
log.error = errorType
log.getLogs = () => history

const infoLine = (name: string, value: any) => {
	return [
		{
			text: `${name} : `,
			style: {
				color: "#37474f",
				fontSize: "25px",
			},
		},
		{
			text: `${value}`,
			style: {
				color: "#7b1fa2",
				fontSize: "25px",
				fontWeight: "bold",
			},
		},
		"\n",
	]
}

export function projectInfo() {
	log.info(
		...[
			...infoLine("Project", info().name),
			...infoLine("Tier", info().tier),
			...infoLine("Environment", info().env),
			...infoLine("Language", info().language),
			...infoLine("Url", info().url?.self),
		],
	)
}

export function warnInfo(
	msg: string,
	instance: ComponentPublicInstance | null,
	trace: string,
) {
	log.warn(
		{
			text: `${msg}`,
			style: {
				color: "#ffffff",
				fontSize: "18px",
				padding: "10px",
				background: "#f9a825",
				borderRadius: "5px",
			},
		},
		"\n",
		{
			text: `${trace}`,
		},
	)
}

export function errorInfo(
	err: any,
	instance: ComponentPublicInstance | null,
	info: string,
) {
	log.error(
		{
			text: `${err?.message ?? err}`,
			style: {
				color: "#ffffff",
				fontSize: "18px",
				padding: "10px",
				background: "#d32f2f",
				borderRadius: "5px",
			},
		},
		"\n\n\n",
		{
			text: `[${info}]`,
			style: {
				color: "#b71c1c",
			},
		},
		"\n",
		{
			text: `${"-".repeat(50)}`,
			style: {
				color: "#ffcdd2",
			},
		},
		"\n\n",
		{
			text: `${err?.stack ?? ""}`,
			style: {
				color: "#f05545",
				borderLeft: "5px solid #b71c1c",
				paddingLeft: "15px",
			},
		},
	)
}
